import React, { useState, Fragment, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'react-bootstrap';
import TextField from './TextField';
import { createPortal } from 'react-dom';
import dropdownIcon from 'assets/images/icons/dropdown-arrow-grey.svg';
import FormButton from './FormButton';
import { useTranslation } from 'react-i18next';

const ConditionalDropdownMenu = React.forwardRef(({ mountTarget, children, ...props }, ref) => {
  const menu = (
    <Dropdown.Menu ref={ref} {...props}>
      {children}
    </Dropdown.Menu>
  );

  return mountTarget === 'body' ? createPortal(menu, document.body) : menu;
});

ConditionalDropdownMenu.displayName = 'ConditionalDropdownMenu';

ConditionalDropdownMenu.propTypes = {
  children: PropTypes.node,
  mountTarget: PropTypes.string,
};

// checkbox node component
const CheckboxGroup = ({ opt, localMap, onToggleCheckbox, onApply, searchTerm, valueLookup }) => {
  const { t } = useTranslation();
  const filteredChildren = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return opt.children;
    return opt.children.filter((c) => c.displayValue.toLowerCase().includes(term));
  }, [opt.children, searchTerm]);

  return (
    <div className='pt-1'>
      {filteredChildren.length > 0 && (
        <div className='d-flex flex-column gap-2'>
          {filteredChildren.map((child) => {
            const isSelected = valueLookup[child.value];
            return (
              <label
                key={child.value}
                className={`d-flex align-items-center dropdown-checkbox-item ${
                  isSelected ? 'selected-option' : ''
                } ${child.disabled ? '' : 'cursor-pointer'}`}
              >
                <input
                  type='checkbox'
                  className={`form-check-input ${child.disabled ? 'cursor-none' : ''}`}
                  checked={!!localMap[child.value]}
                  onChange={() => onToggleCheckbox(child.value)}
                  disabled={!!child.disabled}
                />
                {t(child.displayValue)}
              </label>
            );
          })}
        </div>
      )}

      <FormButton
        text='button_done'
        variant='green-1'
        classes='ms-auto my-1'
        shrink
        minWidth
        onClick={onApply}
      />
    </div>
  );
};

CheckboxGroup.propTypes = {
  opt: PropTypes.object.isRequired,
  localMap: PropTypes.object.isRequired,
  onToggleCheckbox: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  valueLookup: PropTypes.object,
};

function DropdownContent({
  rootNode,
  value,
  name,
  handleChange,
  handleCloseDropdown,
  iconVariant,
}) {
  const { t } = useTranslation();
  const [openNodes, setOpenNodes] = useState({});
  const [childSearch, setChildSearch] = useState({});
  const [localCheckboxes, setLocalCheckboxes] = useState({});
  const [valueLookup, setValueLookup] = useState({});

  useEffect(() => {
    const lookupObj = {};
    value.forEach((v) => {
      lookupObj[v] = true;
    });
    setValueLookup(lookupObj);
  }, []);

  const { parentToChildren, valueToPath } = useMemo(() => {
    const parentToChildren = {};
    const valueToPath = {};

    const walk = (node, path = []) => {
      if (!node.children) return;

      if (node.selectMultiple) {
        parentToChildren[node.value ?? '__root__'] = node.children.map((c) => c.value);
      }

      node.children.forEach((child) => {
        const nextPath = [...path, child.value];
        valueToPath[child.value] = nextPath;
        walk(child, nextPath);
      });
    };

    walk(rootNode);
    return { parentToChildren, valueToPath };
  }, [rootNode]);

  // toggle node
  const toggleNode = useCallback((level, nodeKey) => {
    setOpenNodes((prev) => {
      const updated = { ...prev };

      // Close all siblings at this level
      Object.keys(updated).forEach((key) => {
        if (key.startsWith(level + ':')) {
          delete updated[key];
        }
      });

      // Toggle the clicked node
      const currentKey = level + ':' + nodeKey;
      updated[currentKey] = !prev[currentKey];

      return updated;
    });
  }, []);

  // toggle checkbox
  const toggleCheckbox = useCallback((parent, child) => {
    setLocalCheckboxes((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [child]: !prev[parent]?.[child] },
    }));
  }, []);

  // apply checkbox
  const applyCheckbox = useCallback(
    (parent) => {
      const selected = Object.entries(localCheckboxes[parent] || {})
        .filter(([, v]) => v)
        .map(([k]) => k);

      const children = parentToChildren[parent] || [];
      const sameGroup = value.some((v) => children.includes(v));

      const newValue = sameGroup
        ? [...value.filter((v) => !children.includes(v)), ...selected]
        : selected;

      handleChange(name, newValue);
      handleCloseDropdown();
    },
    [localCheckboxes, parentToChildren, value, handleChange, name],
  );

  // render tree
  const renderNode = useCallback(
    (node, level = 0, path = []) => {
      if (!node.children) return null;

      const nodeKey = node.value ?? '__root__';
      const isRoot = node.value == null;
      const isOpen = isRoot || !!openNodes[level + ':' + nodeKey];

      // SEARCH INPUT
      const searchTerm = childSearch[nodeKey] || '';
      const children =
        !node.searchable || !searchTerm
          ? node.children
          : node.children.filter((c) =>
              c.displayValue.toLowerCase().includes(searchTerm.toLowerCase()),
            );

      return (
        <Fragment key={nodeKey}>
          {node.value && (
            <div
              className='dropdown-item d-flex cursor-pointer'
              // onClick={() => setOpenNodes((p) => ({ ...p, [nodeKey]: !p[nodeKey] }))}
              onClick={() => toggleNode(level, nodeKey)}
            >
              <img src={dropdownIcon} />
              {t(node.displayValue)}
            </div>
          )}

          {isOpen !== false && (
            <div className={level ? 'ps-3' : ''}>
              {node.searchable && (
                <TextField
                  value={searchTerm}
                  name='search'
                  classes='py-2'
                  handleChange={(n, v) => setChildSearch((p) => ({ ...p, [nodeKey]: v }))}
                  placeholder='field_search'
                  shrink
                />
              )}

              {/* handle empty state */}
              {children.length === 0 ? (
                <div className='dropdown-item text-muted no-record-exist d-block text-center'>
                  {t('text_no_results_found')}
                </div>
              ) : node.selectMultiple ? (
                <CheckboxGroup
                  opt={node}
                  localMap={localCheckboxes[nodeKey] || {}}
                  onToggleCheckbox={(child) => toggleCheckbox(nodeKey, child)}
                  onApply={() => applyCheckbox(nodeKey)}
                  searchTerm={searchTerm}
                  valueLookup={valueLookup}
                />
              ) : (
                children.map((child) =>
                  child.children ? (
                    renderNode(child, level + 1, [...path, child.value])
                  ) : (
                    <div
                      key={child.value}
                      className={`dropdown-item cursor-pointer ${
                        valueLookup[child.value] ? 'selected-option' : ''
                      } ${child.disabled ? 'pe-none opacity-50' : ''}`}
                      onClick={() => {
                        handleChange(
                          name,
                          [child.value],
                          [child.displayValue],
                          path.length === 0 ? [name] : path,
                        );
                        handleCloseDropdown();
                      }}
                    >
                      {child.icon && (
                        <>
                          {iconVariant === 'original' || iconVariant === 'muted-original' ? (
                            <img
                              className={`icon ${
                                iconVariant === 'muted-original' ? 'icon-muted-original' : ''
                              }`}
                              src={child.icon}
                              alt='icon'
                            />
                          ) : (
                            <div
                              style={{
                                mask: `url(${child.icon}) no-repeat center / contain`,
                                WebkitMask: `url(${child.icon}) no-repeat center / contain`,
                              }}
                              className='icon icon-muted-theme'
                            />
                          )}
                        </>
                      )}

                      {t(child.displayValue)}
                    </div>
                  ),
                )
              )}
            </div>
          )}
        </Fragment>
      );
    },
    [
      openNodes,
      childSearch,
      localCheckboxes,
      valueLookup,
      toggleCheckbox,
      applyCheckbox,
      handleChange,
      handleCloseDropdown,
    ],
  );

  // auto open nodes when dropdown opens
  useEffect(() => {
    if (!value.length) return;

    const newOpenNodes = {};

    value.forEach((val) => {
      const path = valueToPath[val];
      if (!path) return;

      // prepend root node so level aligns with renderNode
      const fullPath = ['__root__', ...path];

      fullPath.forEach((nodeValue, level) => {
        newOpenNodes[level + ':' + nodeValue] = true;
      });
    });

    setOpenNodes(newOpenNodes);

    // preload checkbox state
    const newLocal = {};
    Object.keys(parentToChildren).forEach((parent) => {
      const childMap = {};
      parentToChildren[parent].forEach((child) => {
        childMap[child] = value.includes(child);
      });
      newLocal[parent] = childMap;
    });

    setLocalCheckboxes(newLocal);
  }, [value, valueToPath, parentToChildren]);

  return (
    <>
      <div className='px-2'>{renderNode(rootNode)}</div>
    </>
  );
}

DropdownContent.propTypes = {
  rootNode: PropTypes.object.isRequired,
  value: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleCloseDropdown: PropTypes.func.isRequired,
  iconVariant: PropTypes.string,
};

// multilevel select main conponent
function MultiLevelSelect({
  toggleComponent,
  classes,
  toggleClasses,
  options,
  name,
  value = [],
  handleChange,
  displayName,
  align,
  disabled,
  mountTarget = 'body',
  fullWidth,
  iconVariant, // iconVariant="original" (always original color), "muted-original" (grey → original on hover), "muted-themed" (grey → theme color on hover)
}) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [popperReady, setPopperReady] = useState(false);
  const menuRef = useRef(null);
  const popperRef = useRef(null);

  // When dropdown opens, keep popper instance reference
  const handleToggle = (isOpen, e, metadata) => {
    setDropdownOpen(isOpen);
    if (metadata && metadata.popper) {
      popperRef.current = metadata.popper;
    }
    if (!isOpen) setPopperReady(false);
  };

  return (
    <div className={`${disabled ? 'pe-none opacity-50' : ''}`}>
      {displayName && <label className='mb-1'>{t(displayName)}</label>}

      <Dropdown show={dropdownOpen} onToggle={handleToggle}>
        <Dropdown.Toggle variant='none' as='div' className={`${toggleClasses ?? ''}`}>
          {toggleComponent}
        </Dropdown.Toggle>

        {dropdownOpen && (
          <ConditionalDropdownMenu
            ref={menuRef}
            mountTarget={mountTarget}
            className={`multi-level-select user-select-none 
            ${popperReady ? 'popper-ready' : 'popper-hidden'} 
            ${classes ?? ''}`}
            align={align ?? 'start'}
            popperConfig={{
              modifiers: [
                ...(fullWidth
                  ? [
                      {
                        name: 'sameWidth',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                          state.styles.popper.width = `${state.rects.reference.width}px`;
                        },
                      },
                    ]
                  : []),
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
                {
                  name: 'flip',
                  options: {
                    boundary: 'viewport',
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: 8,
                  },
                },
                {
                  name: 'computeStyles',
                  options: {
                    adaptive: false,
                  },
                },
                {
                  name: 'captureInstance',
                  enabled: true,
                  phase: 'write',
                  fn: ({ instance, state }) => {
                    popperRef.current = instance;
                    if (state.styles.popper.transform || state.styles.popper.top != null) {
                      setPopperReady(true);
                    }
                  },
                },
              ],
            }}
          >
            <DropdownContent
              rootNode={options}
              value={value}
              name={name}
              handleChange={handleChange}
              handleCloseDropdown={() => {
                setDropdownOpen(false);
                setPopperReady(false);
              }}
              iconVariant={iconVariant}
            />
          </ConditionalDropdownMenu>
        )}
      </Dropdown>
    </div>
  );
}

MultiLevelSelect.propTypes = {
  toggleComponent: PropTypes.node.isRequired,
  classes: PropTypes.string,
  toggleClasses: PropTypes.string,
  options: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  displayName: PropTypes.string,
  align: PropTypes.string,
  disabled: PropTypes.bool,
  mountTarget: PropTypes.string,
  fullWidth: PropTypes.bool,
  iconVariant: PropTypes.string,
};

export default MultiLevelSelect;
